import { type ReactNode, useEffect, useState } from 'react'
import { cx } from '@/utils/strings'

import ShieldLoader from '@/components/ui/shield-loader'
import CtaLink, { buttonStyles } from '../ui/cta-link'

export interface FormProps {
  locale: any
  comments: any
  formName: string
  url: string
  email: string
}
interface Props {
  id: string
  name: string
  afterSubmit: (response) => void
  transformFormData?: (data: FormData) => FormData
  functionPath?: string
  headers?: Record<string, string>
  submitText?: string
  children: ReactNode
  hideButton?: boolean
  className?: string
}

export default function Form({
  id,
  name,
  afterSubmit,
  transformFormData,
  functionPath = '/api/forms',
  submitText = 'Submit',
  headers = { Accept: 'application/json' },
  children,
  hideButton,
  ...props
}: Props): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const [path, setPath] = useState('')
  const [search, setSearch] = useState()

  useEffect(() => {
    setSearch(new URLSearchParams(window.location).get('search'))
    setPath(window.location.pathname)
  }, [])

  useEffect(() => {
    setTimeout(() => {
      const params = new URLSearchParams(search)
      params.forEach((val, key) => {
        document.getElementsByName(key).forEach((el: HTMLInputElement) => {
          el.value = val
          el.dispatchEvent(new Event('change'))
        })
      })
    }, 100)
  }, [])

  return (
    <form
      id={id}
      name={name}
      onSubmit={async (event) => {
        event.preventDefault()

        setIsSubmitting(true)
        setErrorMessages([])

        let formData = new FormData(event.target as HTMLFormElement)
        if (transformFormData) formData = transformFormData(formData)

        const body = headers?.['Content-Type']?.includes('json')
          ? JSON.stringify(Object.fromEntries(formData.entries()))
          : formData

        // timeout if there is no response after a period
        const controller = new AbortController()
        const { signal } = controller
        setTimeout(() => controller.abort(), 31000)

        const response = await fetch(functionPath, {
          method: 'POST',
          body,
          headers,
          signal,
        }).catch((err) => ({
          ok: false,
          statusText: err.message || 'Unknown error',
        }))

        if (response?.ok === false) setErrorMessages([response.statusText])
        else afterSubmit(response)
        setIsSubmitting(false)
      }}
      {...props}
    >
      <input type="hidden" name="formName" value={name} />
      <input type="hidden" name="url" value={path} />
      <input type="hidden" name="locale" value={typeof window !== 'undefined' ? navigator?.language : ''} />
      {children}

      {errorMessages?.length > 0 && (
        <div className="p-4 mt-2 bg-red-600 text-white rounded-2xl text-center">
          <h4>Whoops, we encountered a problem.</h4>
          {errorMessages.map((err, i) => (
            <div key={`error-${i}`} role="alert" className="flex justify-center">
              <span className="font-bwi mr-2">&#xe94a;</span>
              {err}
            </div>
          ))}
        </div>
      )}
      <CtaLink
        asButton
        className={cx(
          'relative bg-primaryBlue text-white mt-4 px-[20px] py-[8px] font-medium leading-normal rounded-full hover:bg-midnight-gradient dark:hover:bg-arctic-gradient dark:text-secondary-800',
          isSubmitting && 'cursor-not-allowed opacity-50',
          hideButton ? 'hidden' : 'block'
        )}
        type="submit"
        style={buttonStyles.btnSolid}
      >
        <div className={cx(isSubmitting ? 'inline-block absolute left-[calc(50%-10px)] top-[10px]' : 'hidden')}>
          <ShieldLoader shieldSize="Xsmall" className="text-white" />
        </div>

        <span className={cx(isSubmitting && 'opacity-0')}>{submitText}</span>
      </CtaLink>
    </form>
  )
}
